import React, {
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../ConfirmModal';
import { FORM_REGISTRY } from '../../forms/registry';
import FormWindowShell from './FormWindowShell';
import FormWindowDock from './FormWindowDock';

const FormWindowContext = createContext(null);

/**
 * `openForm('lead.create')` from anywhere in the app. Also exposes
 * `isFormOpen(...)` so a page can reflect that a form is already parked.
 */
export function useFormWindow() {
  const ctx = useContext(FormWindowContext);
  if (!ctx) throw new Error('useFormWindow must be used inside <FormWindowProvider>');
  return ctx;
}

/** One window per (form, record) — reopening "Add Lead" returns to the draft. */
function windowId(formKey, recordId) {
  return `${formKey}:${recordId ?? 'new'}`;
}

/** `/tags/:tagId/edit` + 7 → `/tags/7/edit`. */
function resolveFullPagePath(def, recordId) {
  if (!def.fullPagePath) return null;
  if (!def.paramKey) return def.fullPagePath;
  return def.fullPagePath.replace(`:${def.paramKey}`, String(recordId));
}

function FormFallback() {
  const { t } = useTranslation();

  return (
    <div className="fw-skel" role="status" aria-live="polite">
      <span className="sr-only">{t('app.loading')}</span>
      <span className="fw-skel-line" style={{ width: '38%' }} />
      <span className="fw-skel-field" />
      <span className="fw-skel-line" style={{ width: '30%' }} />
      <span className="fw-skel-field" />
      <span className="fw-skel-line" style={{ width: '44%' }} />
      <span className="fw-skel-field" />
    </div>
  );
}

/**
 * The window manager for every Add/Edit form in the app.
 *
 * Mounted ABOVE the router, which is the point: a form is a piece of work in
 * progress, not a property of the page you happened to be on, so a half-typed
 * "Add Lead" survives navigating to Organizations and is still parked in the
 * dock when you come back.
 *
 * The model is deliberately ONE visible window plus a dock of parked ones —
 * opening a second form parks the first rather than stacking overlapping
 * panels. That gives the user everything they asked for (park a draft, work on
 * something else, come back) without a drag-and-drop window manager nobody
 * asked for, and it keeps exactly one dialog holding focus at a time.
 *
 * Every window stays MOUNTED while parked (`hidden`, not unmounted) — that is
 * what preserves the draft. Unmounting and restoring from serialised values
 * would silently drop anything the form holds that is not a plain field.
 */
export default function FormWindowProvider({ children }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [windows, setWindows] = useState([]);

  // Mirror of `windows` so the callbacks below can read the current list
  // without taking it as a dependency — otherwise every action identity
  // changes on each state update and every consumer re-renders.
  const windowsRef = useRef(windows);
  useEffect(() => { windowsRef.current = windows; }, [windows]);

  const openForm = useCallback((formKey, options = {}) => {
    const def = FORM_REGISTRY[formKey];
    if (!def) return;

    const { recordId = null, record = null, initialValues = null } = options;
    const id = windowId(formKey, recordId);

    setWindows((prev) => {
      const existing = prev.find((w) => w.id === id);
      // Reopening a form that is already parked restores that draft rather
      // than starting a second one over the top of it.
      const rest = prev
        .filter((w) => w.id !== id)
        .map((w) => ({ ...w, minimized: true }));
      const next = existing
        ? { ...existing, minimized: false }
        : {
          id,
          formKey,
          recordId,
          record,
          initialValues,
          minimized: false,
          maximized: false,
          apiRef: { current: null },
        };
      return [...rest, next];
    });
  }, []);

  const removeWindow = useCallback((id) => {
    setWindows((prev) => prev.filter((w) => w.id !== id));
  }, []);

  const minimizeForm = useCallback((id) => {
    setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, minimized: true } : w)));
  }, []);

  const restoreForm = useCallback((id) => {
    setWindows((prev) => {
      const target = prev.find((w) => w.id === id);
      if (!target) return prev;
      const rest = prev.filter((w) => w.id !== id).map((w) => ({ ...w, minimized: true }));
      return [...rest, { ...target, minimized: false }];
    });
  }, []);

  const toggleMaximize = useCallback((id) => {
    setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, maximized: !w.maximized } : w)));
  }, []);

  // Closing DISCARDS. Everything else in this shell is non-destructive, so the
  // one destructive control is the one that asks — but only when there is
  // actually something to lose.
  const closeForm = useCallback(async (id) => {
    const target = windowsRef.current.find((w) => w.id === id);
    if (!target) return;

    if (target.apiRef.current?.isDirty?.()) {
      const ok = await confirm({
        title: t('formWindow.discardTitle'),
        message: t('formWindow.discardMessage'),
        confirmLabel: t('formWindow.discardConfirm'),
        danger: true,
      });
      if (!ok) return;
    }
    removeWindow(id);
  }, [confirm, t, removeWindow]);

  // "View full page" carries the draft with it. Without this the control would
  // be a trap: click it three fields in and watch the three fields vanish.
  const openFullPage = useCallback((id) => {
    const target = windowsRef.current.find((w) => w.id === id);
    if (!target) return;

    const def = FORM_REGISTRY[target.formKey];
    const path = resolveFullPagePath(def, target.recordId);
    if (!path) return;

    const draft = target.apiRef.current?.getValues?.() ?? target.initialValues ?? null;
    removeWindow(id);
    navigate(path, { state: { draft, record: target.record } });
  }, [navigate, removeWindow]);

  const isFormOpen = useCallback(
    (formKey, recordId = null) => windowsRef.current.some((w) => w.id === windowId(formKey, recordId)),
    [],
  );

  const value = useMemo(
    () => ({ openForm, closeForm, minimizeForm, restoreForm, isFormOpen }),
    [openForm, closeForm, minimizeForm, restoreForm, isFormOpen],
  );

  // Exactly one visible window: the most recently activated non-parked one.
  const activeId = useMemo(() => {
    for (let i = windows.length - 1; i >= 0; i -= 1) {
      if (!windows[i].minimized) return windows[i].id;
    }
    return null;
  }, [windows]);

  const parked = useMemo(
    () => windows
      .filter((w) => w.minimized)
      .map((w) => ({ id: w.id, title: t(FORM_REGISTRY[w.formKey].titleKey) })),
    [windows, t],
  );

  return (
    <FormWindowContext.Provider value={value}>
      {children}

      {windows.map((win) => {
        const def = FORM_REGISTRY[win.formKey];
        const { Component } = def;
        const hidden = win.id !== activeId;

        return (
          <FormWindowShell
            key={win.id}
            title={t(def.titleKey)}
            width={def.width ?? 'md'}
            maximized={win.maximized}
            hidden={hidden}
            canOpenFullPage={Boolean(def.fullPagePath)}
            onMinimize={() => minimizeForm(win.id)}
            onToggleMaximize={() => toggleMaximize(win.id)}
            onOpenFullPage={() => openFullPage(win.id)}
            onClose={() => closeForm(win.id)}
          >
            <Suspense fallback={<FormFallback />}>
              <Component
                surface="modal"
                recordId={win.recordId}
                record={win.record}
                initialValues={win.initialValues}
                apiRef={win.apiRef}
                onDone={() => removeWindow(win.id)}
                onCancel={() => closeForm(win.id)}
              />
            </Suspense>
          </FormWindowShell>
        );
      })}

      <FormWindowDock windows={parked} onRestore={restoreForm} onClose={closeForm} />
    </FormWindowContext.Provider>
  );
}
